import React, { useEffect, useState } from 'react';
import { BrainIcon, XIcon, PlusCircleIcon, CheckIcon, AlertTriangleIcon, SearchIcon } from 'lucide-react';
interface Step4SkillsExtractionProps {
  formData: any;
  updateFormData: (data: any) => void;
}
interface Skill {
  id: string;
  name: string;
  category: 'Technical' | 'Soft' | 'Industry' | 'Other';
  confidence: number;
  selected: boolean;
}
const Step4SkillsExtraction: React.FC<Step4SkillsExtractionProps> = ({
  formData,
  updateFormData
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [newSkill, setNewSkill] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Suggested skills for autocomplete
  const suggestedSkills = ['Project Management', 'Leadership', 'Strategic Planning', 'Data Analysis', 'Critical Thinking', 'Problem Solving', 'Communication', 'Teamwork', 'Python', 'JavaScript', 'Machine Learning', 'Financial Analysis', 'Marketing Strategy', 'UX Design', 'Content Creation', 'SEO', 'Supply Chain Management', 'Human Resources', 'Public Speaking'].filter(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()) && !skills.some(s => s.name.toLowerCase() === skill.toLowerCase()));
  // Mock AI extraction process
  useEffect(() => {
    if (formData.extractedSkills?.length) {
      setSkills(formData.extractedSkills);
      setIsLoading(false);
      return;
    }
    // Simulate AI extraction with a delay
    const timer = setTimeout(() => {
      // Extract skills based on program type, description, and learning outcomes
      const extractedSkills = generateMockSkills(formData);
      setSkills(extractedSkills);
      updateFormData({
        extractedSkills
      });
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  // Generate mock skills based on program data
  const generateMockSkills = (data: any): Skill[] => {
    const mockSkills: Skill[] = [];
    // Technical skills based on program type
    if (data.programType === "Master's" || data.programType === "Bachelor's") {
      if (data.programName?.toLowerCase().includes('business') || data.description?.toLowerCase().includes('business')) {
        mockSkills.push({
          id: 'tech-1',
          name: 'Financial Analysis',
          category: 'Technical',
          confidence: 0.95,
          selected: true
        }, {
          id: 'tech-2',
          name: 'Business Intelligence',
          category: 'Technical',
          confidence: 0.92,
          selected: true
        }, {
          id: 'tech-3',
          name: 'Strategic Planning',
          category: 'Technical',
          confidence: 0.88,
          selected: true
        }, {
          id: 'tech-4',
          name: 'Market Research',
          category: 'Technical',
          confidence: 0.85,
          selected: true
        }, {
          id: 'tech-5',
          name: 'Data Analytics',
          category: 'Technical',
          confidence: 0.78,
          selected: true
        });
      } else if (data.programName?.toLowerCase().includes('computer') || data.description?.toLowerCase().includes('technology')) {
        mockSkills.push({
          id: 'tech-6',
          name: 'Programming',
          category: 'Technical',
          confidence: 0.96,
          selected: true
        }, {
          id: 'tech-7',
          name: 'Database Management',
          category: 'Technical',
          confidence: 0.91,
          selected: true
        }, {
          id: 'tech-8',
          name: 'Software Development',
          category: 'Technical',
          confidence: 0.89,
          selected: true
        }, {
          id: 'tech-9',
          name: 'Network Security',
          category: 'Technical',
          confidence: 0.82,
          selected: true
        }, {
          id: 'tech-10',
          name: 'Cloud Computing',
          category: 'Technical',
          confidence: 0.76,
          selected: true
        });
      } else {
        mockSkills.push({
          id: 'tech-11',
          name: 'Research Methodology',
          category: 'Technical',
          confidence: 0.9,
          selected: true
        }, {
          id: 'tech-12',
          name: 'Data Analysis',
          category: 'Technical',
          confidence: 0.87,
          selected: true
        }, {
          id: 'tech-13',
          name: 'Project Management',
          category: 'Technical',
          confidence: 0.85,
          selected: true
        });
      }
    }
    // Soft skills common to most programs
    mockSkills.push({
      id: 'soft-1',
      name: 'Critical Thinking',
      category: 'Soft',
      confidence: 0.94,
      selected: true
    }, {
      id: 'soft-2',
      name: 'Problem Solving',
      category: 'Soft',
      confidence: 0.92,
      selected: true
    }, {
      id: 'soft-3',
      name: 'Communication',
      category: 'Soft',
      confidence: 0.9,
      selected: true
    }, {
      id: 'soft-4',
      name: 'Teamwork',
      category: 'Soft',
      confidence: 0.88,
      selected: true
    }, {
      id: 'soft-5',
      name: 'Leadership',
      category: 'Soft',
      confidence: 0.85,
      selected: true
    }, {
      id: 'soft-6',
      name: 'Time Management',
      category: 'Soft',
      confidence: 0.82,
      selected: true
    });
    // Industry-specific skills
    if (data.programName?.toLowerCase().includes('business') || data.description?.toLowerCase().includes('business')) {
      mockSkills.push({
        id: 'ind-1',
        name: 'Business Administration',
        category: 'Industry',
        confidence: 0.93,
        selected: true
      }, {
        id: 'ind-2',
        name: 'Corporate Finance',
        category: 'Industry',
        confidence: 0.89,
        selected: true
      }, {
        id: 'ind-3',
        name: 'Management Consulting',
        category: 'Industry',
        confidence: 0.84,
        selected: true
      }, {
        id: 'ind-4',
        name: 'Entrepreneurship',
        category: 'Industry',
        confidence: 0.81,
        selected: true
      });
    } else if (data.programName?.toLowerCase().includes('computer') || data.description?.toLowerCase().includes('technology')) {
      mockSkills.push({
        id: 'ind-5',
        name: 'Software Engineering',
        category: 'Industry',
        confidence: 0.94,
        selected: true
      }, {
        id: 'ind-6',
        name: 'IT Infrastructure',
        category: 'Industry',
        confidence: 0.88,
        selected: true
      }, {
        id: 'ind-7',
        name: 'Systems Analysis',
        category: 'Industry',
        confidence: 0.86,
        selected: true
      }, {
        id: 'ind-8',
        name: 'Cybersecurity',
        category: 'Industry',
        confidence: 0.83,
        selected: true
      });
    } else if (data.programName?.toLowerCase().includes('marketing') || data.description?.toLowerCase().includes('marketing')) {
      mockSkills.push({
        id: 'ind-9',
        name: 'Digital Marketing',
        category: 'Industry',
        confidence: 0.92,
        selected: true
      }, {
        id: 'ind-10',
        name: 'Content Strategy',
        category: 'Industry',
        confidence: 0.87,
        selected: true
      }, {
        id: 'ind-11',
        name: 'Brand Management',
        category: 'Industry',
        confidence: 0.85,
        selected: true
      }, {
        id: 'ind-12',
        name: 'Social Media Marketing',
        category: 'Industry',
        confidence: 0.8,
        selected: true
      });
    }
    // Add some skills based on learning outcomes if available
    if (data.learningOutcomes?.length > 0) {
      data.learningOutcomes.forEach((outcome: string, index: number) => {
        if (outcome.toLowerCase().includes('analy')) {
          mockSkills.push({
            id: `outcome-${index}-1`,
            name: 'Analytical Thinking',
            category: 'Soft',
            confidence: 0.88,
            selected: true
          });
        }
        if (outcome.toLowerCase().includes('lead')) {
          mockSkills.push({
            id: `outcome-${index}-2`,
            name: 'Leadership Development',
            category: 'Soft',
            confidence: 0.86,
            selected: true
          });
        }
        if (outcome.toLowerCase().includes('research')) {
          mockSkills.push({
            id: `outcome-${index}-3`,
            name: 'Research Skills',
            category: 'Technical',
            confidence: 0.9,
            selected: true
          });
        }
      });
    }
    // Remove duplicates by name
    const uniqueSkills = mockSkills.filter((skill, index, self) => index === self.findIndex(s => s.name === skill.name));
    return uniqueSkills;
  };
  // Handle toggling a skill's selection
  const toggleSkill = (id: string) => {
    const updatedSkills = skills.map(skill => skill.id === id ? {
      ...skill,
      selected: !skill.selected
    } : skill);
    setSkills(updatedSkills);
    updateFormData({
      extractedSkills: updatedSkills
    });
  };
  // Handle adding a new skill
  const handleAddSkill = (skillName: string = newSkill.trim()) => {
    if (!skillName) return;
    // Check if skill already exists
    if (skills.some(skill => skill.name.toLowerCase() === skillName.toLowerCase())) {
      return;
    }
    const newSkillObj: Skill = {
      id: `manual-${Date.now()}`,
      name: skillName,
      category: 'Other',
      confidence: 1.0,
      selected: true
    };
    const updatedSkills = [...skills, newSkillObj];
    setSkills(updatedSkills);
    updateFormData({
      extractedSkills: updatedSkills
    });
    setNewSkill('');
    setSearchQuery('');
    setShowSuggestions(false);
  };
  // Handle selecting a suggested skill
  const handleSelectSuggestion = (skill: string) => {
    handleAddSkill(skill);
  };
  // Get confidence level class
  const getConfidenceClass = (confidence: number) => {
    if (confidence >= 0.9) return 'bg-green-100 text-green-800';
    if (confidence >= 0.7) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  // Get confidence level text
  const getConfidenceText = (confidence: number) => {
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  };
  // Group skills by category
  const groupedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);
  // Get selected skills count
  const selectedSkillsCount = skills.filter(skill => skill.selected).length;
  return <div data-id="element-654">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-655">
        Skills Extraction & Confirmation
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-656">
        Our AI has analyzed your program details to identify relevant skills
        that students will develop. Review and confirm these skills, or add your
        own.
      </p>
      {isLoading ? <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200" data-id="element-657">
          <div className="flex items-center justify-center space-x-2 mb-4" data-id="element-658">
            <BrainIcon className="w-6 h-6 text-primary-500 animate-pulse" data-id="element-659" />
            <div className="h-4 w-4 bg-primary-500 rounded-full animate-bounce" style={{
          animationDelay: '0.2s'
        }} data-id="element-660"></div>
            <div className="h-4 w-4 bg-primary-500 rounded-full animate-bounce" style={{
          animationDelay: '0.4s'
        }} data-id="element-661"></div>
            <div className="h-4 w-4 bg-primary-500 rounded-full animate-bounce" style={{
          animationDelay: '0.6s'
        }} data-id="element-662"></div>
          </div>
          <p className="text-gray-600 font-medium" data-id="element-663">
            Analyzing program content to extract skills...
          </p>
          <p className="text-gray-500 text-sm mt-2" data-id="element-664">
            This may take a few moments
          </p>
        </div> : <div className="space-y-6" data-id="element-665">
          {/* Add new skill */}
          <div className="relative" data-id="element-666">
            <div className="flex" data-id="element-667">
              <div className="relative flex-grow" data-id="element-668">
                <input type="text" value={searchQuery} onChange={e => {
              setSearchQuery(e.target.value);
              setNewSkill(e.target.value);
              setShowSuggestions(true);
            }} onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} placeholder="Add a new skill..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-669" />
                <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-670" />
              </div>
              <button type="button" onClick={() => handleAddSkill()} className="px-4 py-2 bg-primary-500 text-white rounded-r-md hover:bg-primary-600" data-id="element-671">
                Add
              </button>
            </div>
            {showSuggestions && searchQuery && suggestedSkills.length > 0 && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-id="element-672">
                <ul className="py-1" data-id="element-673">
                  {suggestedSkills.map((skill, index) => <li key={index} className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm" onClick={() => handleSelectSuggestion(skill)} data-id="element-674">
                      {skill}
                    </li>)}
                </ul>
              </div>}
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-675">
            <div className="flex items-center justify-between mb-4" data-id="element-676">
              <h3 className="text-md font-medium text-gray-800" data-id="element-677">
                Program Skills ({selectedSkillsCount}/{skills.length})
              </h3>
              <div className="flex items-center space-x-3" data-id="element-678">
                <div className="flex items-center" data-id="element-679">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-1" data-id="element-680"></div>
                  <span className="text-xs text-gray-600" data-id="element-681">High confidence</span>
                </div>
                <div className="flex items-center" data-id="element-682">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-683"></div>
                  <span className="text-xs text-gray-600" data-id="element-684">
                    Medium confidence
                  </span>
                </div>
                <div className="flex items-center" data-id="element-685">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-686"></div>
                  <span className="text-xs text-gray-600" data-id="element-687">Low confidence</span>
                </div>
              </div>
            </div>
            {/* Technical Skills */}
            {groupedSkills['Technical']?.length > 0 && <div className="mb-6" data-id="element-688">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-689">
                  <span className="inline-block w-5 h-5 rounded-full bg-blue-100 text-blue-800 text-xs flex items-center justify-center mr-2" data-id="element-690">
                    T
                  </span>
                  Technical Skills
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-691">
                  {groupedSkills['Technical'].map(skill => <div key={skill.id} className={`group flex items-center px-3 py-1.5 rounded-full text-sm ${skill.selected ? 'bg-white text-primary-500 border border-primary-500' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-692">
                      <span data-id="element-693">{skill.name}</span>
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${getConfidenceClass(skill.confidence)}`} title={`${Math.round(skill.confidence * 100)}% confidence`} data-id="element-694">
                        {getConfidenceText(skill.confidence)}
                      </span>
                      <button type="button" onClick={() => toggleSkill(skill.id)} className={`ml-1.5 p-0.5 rounded-full ${skill.selected ? 'text-primary-500 hover:bg-primary-500 hover:text-white' : 'text-gray-400 hover:bg-gray-200'}`} data-id="element-695">
                        {skill.selected ? <XIcon className="w-3.5 h-3.5" data-id="element-696" /> : <PlusCircleIcon className="w-3.5 h-3.5" data-id="element-697" />}
                      </button>
                    </div>)}
                </div>
              </div>}
            {/* Soft Skills */}
            {groupedSkills['Soft']?.length > 0 && <div className="mb-6" data-id="element-698">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-699">
                  <span className="inline-block w-5 h-5 rounded-full bg-purple-100 text-purple-800 text-xs flex items-center justify-center mr-2" data-id="element-700">
                    S
                  </span>
                  Soft Skills
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-701">
                  {groupedSkills['Soft'].map(skill => <div key={skill.id} className={`group flex items-center px-3 py-1.5 rounded-full text-sm ${skill.selected ? 'bg-white text-primary-500 border border-primary-500' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-702">
                      <span data-id="element-703">{skill.name}</span>
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${getConfidenceClass(skill.confidence)}`} title={`${Math.round(skill.confidence * 100)}% confidence`} data-id="element-704">
                        {getConfidenceText(skill.confidence)}
                      </span>
                      <button type="button" onClick={() => toggleSkill(skill.id)} className={`ml-1.5 p-0.5 rounded-full ${skill.selected ? 'text-primary-500 hover:bg-primary-500 hover:text-white' : 'text-gray-400 hover:bg-gray-200'}`} data-id="element-705">
                        {skill.selected ? <XIcon className="w-3.5 h-3.5" data-id="element-706" /> : <PlusCircleIcon className="w-3.5 h-3.5" data-id="element-707" />}
                      </button>
                    </div>)}
                </div>
              </div>}
            {/* Industry Skills */}
            {groupedSkills['Industry']?.length > 0 && <div className="mb-6" data-id="element-708">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-709">
                  <span className="inline-block w-5 h-5 rounded-full bg-green-100 text-green-800 text-xs flex items-center justify-center mr-2" data-id="element-710">
                    I
                  </span>
                  Industry-specific Skills
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-711">
                  {groupedSkills['Industry'].map(skill => <div key={skill.id} className={`group flex items-center px-3 py-1.5 rounded-full text-sm ${skill.selected ? 'bg-white text-primary-500 border border-primary-500' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-712">
                      <span data-id="element-713">{skill.name}</span>
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${getConfidenceClass(skill.confidence)}`} title={`${Math.round(skill.confidence * 100)}% confidence`} data-id="element-714">
                        {getConfidenceText(skill.confidence)}
                      </span>
                      <button type="button" onClick={() => toggleSkill(skill.id)} className={`ml-1.5 p-0.5 rounded-full ${skill.selected ? 'text-primary-500 hover:bg-primary-500 hover:text-white' : 'text-gray-400 hover:bg-gray-200'}`} data-id="element-715">
                        {skill.selected ? <XIcon className="w-3.5 h-3.5" data-id="element-716" /> : <PlusCircleIcon className="w-3.5 h-3.5" data-id="element-717" />}
                      </button>
                    </div>)}
                </div>
              </div>}
            {/* Other Skills */}
            {groupedSkills['Other']?.length > 0 && <div data-id="element-718">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-719">
                  <span className="inline-block w-5 h-5 rounded-full bg-gray-100 text-gray-800 text-xs flex items-center justify-center mr-2" data-id="element-720">
                    O
                  </span>
                  Other Skills
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-721">
                  {groupedSkills['Other'].map(skill => <div key={skill.id} className={`group flex items-center px-3 py-1.5 rounded-full text-sm ${skill.selected ? 'bg-white text-primary-500 border border-primary-500' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-722">
                      <span data-id="element-723">{skill.name}</span>
                      <button type="button" onClick={() => toggleSkill(skill.id)} className={`ml-1.5 p-0.5 rounded-full ${skill.selected ? 'text-primary-500 hover:bg-primary-500 hover:text-white' : 'text-gray-400 hover:bg-gray-200'}`} data-id="element-724">
                        {skill.selected ? <XIcon className="w-3.5 h-3.5" data-id="element-725" /> : <PlusCircleIcon className="w-3.5 h-3.5" data-id="element-726" />}
                      </button>
                    </div>)}
                </div>
              </div>}
            {skills.length === 0 && <div className="text-center py-8" data-id="element-727">
                <AlertTriangleIcon className="w-12 h-12 text-yellow-400 mx-auto mb-3" data-id="element-728" />
                <h3 className="text-gray-700 font-medium mb-1" data-id="element-729">
                  No skills extracted
                </h3>
                <p className="text-gray-500 text-sm mb-4" data-id="element-730">
                  We couldn't extract any skills from your program data. Try
                  adding skills manually.
                </p>
              </div>}
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4" data-id="element-731">
            <div className="flex items-start" data-id="element-732">
              <BrainIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-733" />
              <div data-id="element-734">
                <h3 className="font-medium text-blue-800 mb-1" data-id="element-735">
                  Why are skills important?
                </h3>
                <p className="text-sm text-blue-700" data-id="element-736">
                  Skills help students understand what they'll gain from your
                  program. They also improve program discoverability in searches
                  and help match your program with qualified candidates.
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default Step4SkillsExtraction;